import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { translations } from '../locales/languages';
import RankingBoard from './RankingBoard';

const WordRainLobby = ({ language, onStart, unlockedLevel = 1, user }) => {
    const [selectedLevel, setSelectedLevel] = useState(1);
    const [mode, setMode] = useState('hangeul');
    const [category, setCategory] = useState('K-Food');

    // 카테고리 목록
    const categories = ['K-Food'];

    // App.js에서 전달받은 language 상태에 따라 즉시 데이터 동기화
    const t = translations[language] || translations.ko;
    const lobbyData = t.lobby;

    // 닉네임 로직
    const userNickname = user?.email?.split('@')[0] || localStorage.getItem('user_nickname') || 'Guest';

    // 환영 메시지 구성
    let welcomeMessage = '';
    if (lobbyData && lobbyData.welcome) {
        if (language === 'ko' || language === 'jp') {
            welcomeMessage = `${userNickname}${lobbyData.welcome}`;
        } else {
            welcomeMessage = `${lobbyData.welcome}${userNickname}!`;
        }
    }

    if (!lobbyData) return <div className="text-white text-center py-20">Loading...</div>;

    return (
        <div className="relative min-h-[75vh] py-4 px-4 md:px-6 overflow-hidden bg-gradient-to-br from-slate-900 via-zinc-900 to-slate-800">
            {/* 프리미엄 배경 효과 - 고급스러운 톤 */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {/* 따뜻한 그래디언트 */}
                <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/8 rounded-full blur-[150px]" />
                <div className="absolute top-1/4 right-1/3 w-96 h-96 bg-rose-500/8 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-emerald-500/8 rounded-full blur-[150px]" />
                {/* 세련된 그리드 패턴 */}
                <div className="absolute inset-0 opacity-[0.02] bg-[linear-gradient(rgba(255,255,255,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.5)_1px,transparent_1px)] bg-[length:50px_50px]" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* 간결한 헤더 - 여백 최소화 */}
                <div className="text-center mb-6 animate-in fade-in slide-in-from-bottom-8 duration-700">
                    <motion.h1
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45 }}
                        className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-1 leading-snug bg-gradient-to-br from-white via-amber-100 to-amber-200 bg-clip-text text-transparent"
                    >
                        {lobbyData.title}
                    </motion.h1>

                    <motion.div
                        id="welcome-msg"
                        className="mb-2 text-sm md:text-base font-medium text-amber-300/95 tracking-normal"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.12, duration: 0.5 }}
                    >
                        {welcomeMessage}
                    </motion.div>

                    <p className="text-gray-300 text-sm md:text-base font-light max-w-2xl mx-auto leading-relaxed mb-4">
                        {lobbyData.desc}
                    </p>
                </div>

                {/* 레벨 카드 섹션 - 한 화면에 모두 표시 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 w-full mb-10">
                    {lobbyData.levels.map((l, idx) => {
                        const isActuallyLocked = l.id > unlockedLevel;
                        const isSelected = selectedLevel === l.id;

                        return (
                            <motion.div
                                key={l.id}
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.08, duration: 0.4 }}
                                onClick={() => !isActuallyLocked && setSelectedLevel(l.id)}
                                className={`relative p-4 rounded-2xl border backdrop-blur-xl transition-all duration-300 cursor-pointer group overflow-hidden ${
                                    isSelected
                                        ? 'border-amber-500/50 bg-gradient-to-br from-amber-500/20 to-amber-600/5 shadow-lg shadow-amber-500/20'
                                        : isActuallyLocked
                                        ? 'border-white/5 bg-white/[0.02] opacity-40 cursor-not-allowed'
                                        : 'border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] hover:border-white/20 hover:from-white/[0.12]'
                                }`}
                            >
                                {/* 배경 글로우 */}
                                {!isActuallyLocked && (
                                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-rose-500/0 group-hover:from-amber-500/5 group-hover:to-rose-500/5 transition-all duration-300" />
                                )}

                                <div className="relative">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">{'🎯🌧️🔥⚡💎👑'[l.id - 1]}</span>
                                            <span className="text-xs font-black px-2 py-1 bg-white/10 rounded-lg uppercase tracking-wider text-white">Level {String(l.id).padStart(2, '0')}</span>
                                        </div>
                                        {isActuallyLocked && <span className="text-[10px] text-red-400 font-bold">🔒</span>}
                                        {isSelected && <div className="w-2 h-2 rounded-full bg-gradient-to-br from-amber-400 to-rose-400 shadow-lg" />}
                                    </div>
                                    
                                    <h3 className="text-base font-extrabold mb-1 text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-amber-300 group-hover:to-rose-300 group-hover:bg-clip-text transition-all duration-300">
                                        {l.name}
                                    </h3>
                                    
                                    <p className="text-gray-400 text-xs font-light leading-relaxed mb-3">
                                        {l.desc}
                                    </p>

                                    {/* 진행도 표시기 */}
                                    <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                                        <motion.div 
                                            className="h-full bg-gradient-to-r from-amber-500 to-rose-500"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${l.id * 16.67}%` }}
                                            transition={{ delay: idx * 0.08 + 0.2, duration: 0.7 }}
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* 게임 모드 섹션 */}
                <div className="mb-10">
                    <p className="text-gray-400 text-xs font-light tracking-wider mb-3 ml-1 uppercase">Game Mode</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        {[
                            { id: 'hangeul', label: lobbyData.modeHangeul, icon: '🔤' },
                            { id: 'meaning', label: lobbyData.modeMeaning, icon: '💭', disabled: selectedLevel === 1 }
                        ].map((m) => (
                            <motion.button
                                key={m.id}
                                onClick={() => !m.disabled && setMode(m.id)}
                                whileHover={!m.disabled ? { scale: 1.02 } : {}}
                                whileTap={!m.disabled ? { scale: 0.98 } : {}}
                                className={`flex-1 group relative px-6 py-3 rounded-xl font-bold text-sm tracking-wider uppercase transition-all duration-300 ${
                                    m.disabled
                                        ? 'opacity-30 cursor-not-allowed bg-white/5 border border-white/5'
                                        : mode === m.id
                                        ? 'bg-gradient-to-br from-amber-600 to-rose-600 text-white border border-amber-400/30 shadow-lg shadow-amber-500/20'
                                        : 'bg-white/[0.08] border border-white/10 text-gray-300 hover:bg-white/[0.12] hover:border-white/20'
                                }`}
                            >
                                <span className="text-lg mr-2">{m.icon}</span>
                                {m.label}
                            </motion.button>
                        ))}
                    </div>
                </div>

                {/* 카테고리 선택 섹션 */}
                <div className="mb-10">
                    <p className="text-gray-400 text-xs font-light tracking-wider mb-3 ml-1 uppercase">Category</p>
                    <div className="flex flex-wrap gap-2">
                        {categories.map((cat) => (
                            <motion.button
                                key={cat}
                                onClick={() => setCategory(cat)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={`px-5 py-2 rounded-lg font-bold text-xs tracking-wider uppercase transition-all duration-300 border ${
                                    category === cat
                                        ? 'bg-gradient-to-r from-emerald-600 to-cyan-600 text-white border-emerald-400/30 shadow-lg shadow-emerald-500/20'
                                        : 'bg-white/[0.08] border-white/10 text-gray-300 hover:bg-white/[0.12] hover:border-white/20'
                                }`}
                            >
                                {cat}
                            </motion.button>
                        ))}
                    </div>
                </div>

                {/* 액션 버튼 섹션 */}
                <div className="flex flex-col sm:flex-row gap-3 mb-12 w-full max-w-3xl mx-auto">
                    <motion.button
                        id="start-btn"
                        onClick={() => onStart({ level: selectedLevel, mode, category })}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-1 group relative px-6 py-4 rounded-xl bg-gradient-to-br from-amber-600 to-rose-600 text-white font-black text-base tracking-[0.15em] uppercase hover:from-amber-500 hover:to-rose-500 transition-all duration-300 shadow-lg shadow-amber-500/30 border border-amber-400/30 overflow-hidden"
                    >
                        <span className="relative z-10 flex items-center justify-center gap-2">
                            🚀 {lobbyData.startBtn}
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-400/0 via-white/10 to-rose-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -translate-x-full group-hover:translate-x-full" />
                    </motion.button>

                    <motion.button
                        id="rank-btn"
                        onClick={() => {
                            const rankSection = document.getElementById('ranking-board-section');
                            if (rankSection) rankSection.scrollIntoView({ behavior: 'smooth' });
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-1 px-6 py-4 rounded-xl border border-white/20 bg-white/[0.08] text-white font-bold text-base tracking-[0.1em] uppercase hover:bg-white/[0.12] hover:border-white/30 transition-all duration-300"
                    >
                        📊 {lobbyData.rankBtn || 'RANKINGS'}
                    </motion.button>

                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-1 px-6 py-4 rounded-xl border border-white/20 bg-white/[0.08] text-white font-bold text-base tracking-[0.1em] uppercase hover:bg-white/[0.12] hover:border-white/30 transition-all duration-300"
                    >
                        👤 {lobbyData.profileSet || 'PROFILE'}
                    </motion.button>
                </div>

                {/* 글로벌 랭킹 보드 */}
                <div id="ranking-board-section" className="w-full animate-in fade-in slide-in-from-bottom-8 duration-1000">
                    <div className="mb-10 text-center">
                        <p className="text-gray-400 text-xs font-light tracking-wider mb-3 uppercase">Global Leaderboard</p>
                        <div className="inline-block px-4 py-1 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-lg border border-white/10">
                            <span className="text-xs font-bold text-emerald-400 tracking-widest">⚡ LIVE RANKINGS</span>
                        </div>
                    </div>
                    <RankingBoard language={language} />
                </div>
            </div>
        </div>
    );
};

// 핵심 교정: 파일의 주인공으로 컴포넌트를 내보냄
export default WordRainLobby;